//Node Modules
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState, useRecoilState, useRecoilRefresher_UNSTABLE } from "recoil";
import { useForm, Controller } from "react-hook-form";

//BinaryForge Components
import { BfDialog, Note } from "../helpers";

//3rd Party Components
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";

//Atoms
import { dialogAtomFamily } from "../../atoms";
import { toastAtom } from "../../atoms/toast";
import { usersSelector } from "../../atoms/user";

//Helpers
import { useAddUserToScheme } from "../../helpers/User";

//Other
import { nav } from "../../config/navigation";
import { emailValidation, userRoleOptions } from "../../config/user";
const appElement = document.getElementsByClassName("appWrapper")[0];

const AddExistingUserDialog = () => {
	//Hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const addUserToScheme = useAddUserToScheme();

	//Recoil
	const [show, setShow] = useRecoilState(dialogAtomFamily("addExistingUserDialog"));
	const setToast = useSetRecoilState(toastAtom);
	const refreshUserList = useRecoilRefresher_UNSTABLE(usersSelector);

	//Local
	//Kept outside the form errors so the typed values survive a not-found answer
	const [notFound, setNotFound] = useState(false);

	//React Hook Form
	const defaultValues = { email: "", role: null };

	const {
		control,
		getValues,
		formState: { errors, isSubmitting },
		handleSubmit,
		reset,
	} = useForm({ defaultValues, mode: "onTouched", reValidateMode: "onChange" });

	//Start from a clean form each time the dialog is opened
	useEffect(() => {
		if (show) {
			reset(defaultValues);
			setNotFound(false);
		}
	}, [show]);

	// Form Error Message
	const getFormErrorMessage = (name) => {
		return errors[name] && <span className="fontColour-error fontSize-small">{errors[name].message}</span>;
	};

	// Submit the Form
	const onSubmit = async (data) => {
		const email = data.email.trim();
		setNotFound(false);

		const result = await addUserToScheme(email, data.role, t("user.addExisting.loading"));

		switch (result.status) {
			case "added":
				refreshUserList();
				setShow(false);
				setToast({
					severity: "success",
					summary: t("user.addExisting.toast.successSummary"),
					detail: t("user.addExisting.toast.successDetail", { email: email }),
				});
				break;
			//Hold the dialog and the entries open so the owner can switch to Create User
			case "notFound":
				setNotFound(true);
				break;
			case "rolledBack":
				setToast({
					severity: "error",
					summary: t("user.addExisting.toast.rollbackSummary"),
					detail: t("user.addExisting.toast.rollbackDetail", { error: result.error }),
				});
				break;
			case "rollbackFailed":
				setToast({
					severity: "error",
					summary: t("user.addExisting.toast.rollbackSummary"),
					detail: t("user.addExisting.toast.rollbackFailedDetail", { email: email, error: result.error }),
				});
				break;
			default:
				setToast({
					severity: "error",
					summary: t("user.addExisting.toast.errorSummary"),
					detail: result.messageKey
						? t("user.addExisting.toast.errorDetail", { error: result.messageKey })
						: t("user.addExisting.toast.unknownDetail"),
				});
		}
	};

	//Carry the entries over so nothing has to be typed twice
	const createInstead = () => {
		const { email, role } = getValues();
		setShow(false);
		navigate(`${nav.user.base}/create`, { state: { email: email.trim(), role: role } });
	};

	// Footer
	const footer = (
		<>
			<Button label={t("common.action.cancel")} onClick={() => setShow(false)} />
			{notFound && (
				<Button
					label={t("user.addExisting.actions.createInstead")}
					icon="pi pi-user-plus"
					onClick={createInstead}
				/>
			)}
			<Button
				label={t("user.addExisting.actions.submit")}
				className="feature success"
				onClick={() => handleSubmit(onSubmit)()}
				disabled={isSubmitting}
			/>
		</>
	);

	return (
		<BfDialog id="addExistingUserDialog" header={t("user.addExisting.header")} footer={footer}>
			<p className="marginBottom-small">{t("user.addExisting.message")}</p>
			<form>
				<div className="formField">
					<label htmlFor="email">{t("user.addExisting.form.email.label")}</label>
					<Controller
						name="email"
						control={control}
						rules={{
							required: t("common.form.required"),
							pattern: { value: emailValidation, message: t("user.validation.email.pattern") },
						}}
						render={({ field, fieldState }) => (
							<InputText
								{...field}
								placeholder={t("user.addExisting.form.email.placeholder")}
								onChange={(e) => {
									setNotFound(false);
									field.onChange(e.target.value);
								}}
								className={classNames({ "p-error": fieldState.error })}
							/>
						)}
					/>
					{getFormErrorMessage("email")}
				</div>
				<div className="formField marginTop-small">
					<label htmlFor="role">{t("user.addExisting.form.role.label")}</label>
					<Controller
						name="role"
						control={control}
						rules={{ required: t("common.form.required") }}
						render={({ field: { ref, ...newField }, fieldState }) => (
							<Dropdown
								appendTo={appElement}
								{...newField}
								inputRef={ref}
								options={userRoleOptions}
								placeholder={t("user.addExisting.form.role.placeholder")}
								onChange={(e) => newField.onChange(e.value)}
								className={classNames({ "p-error": fieldState.error })}
							/>
						)}
					/>
					{getFormErrorMessage("role")}
				</div>
			</form>
			{notFound && (
				<Note
					wrapperStyle="marginTop-small"
					messageKey={t("user.addExisting.notFound")}
					icon="pi pi-exclamation-triangle fontColour-warn"
				/>
			)}
		</BfDialog>
	);
};

export default AddExistingUserDialog;
